import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Platform,
  Image,
  findNodeHandle,
} from "react-native";
import { SvgXml } from "react-native-svg";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChildStackParamList } from "../../navigation/types";
import { isFactorTv } from "@rnv/renative";
import { useAuthStore } from "../../state/authStore";
import useKeydown from "../../hooks/useKeydown";
import { childrenApi, Video, Child, Progress } from "../../api/children";
import { contentApi, VideoListItem } from "../../api/content";
import { Mascot } from "../../components/Mascot";
import { VideoArt, paletteFor } from "../../components/VideoArt";
import { Star, Clock, Lock, ChevronDown } from "lucide-react-native";
import KidModeLock from "../../components/KidModeLock";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { colors, fontFamily } from "../../theme";

type Props = NativeStackScreenProps<ChildStackParamList, "ChildHome">;

type RowData = {
  key: string;
  title: string;
  items: VideoListItem[];
};

const PROFILE_THEMES: Record<number, { color: string; bg: string }> = {
  0: { color: "#F18EB6", bg: "#FFE0EB" },
  1: { color: "#5B8DEF", bg: "#DCE7FF" },
};

// Heading for the category rail, built from the child's allowed categories.
// Lists up to three ("Numbers, Letters & Shapes"), then falls back to a count
// so a parent who allowed everything doesn't get a heading that runs off-screen.
// No restriction set → the rail is just "more stuff", so keep it generic.
function railTitle(cats: readonly string[]): string {
  const names = cats.map((c) => c.trim()).filter(Boolean);
  if (names.length === 0) return "More to explore";
  if (names.length === 1) return names[0];
  if (names.length <= 3) {
    return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 2).join(", ")} & ${names.length - 2} more`;
}


export default function ChildHomeScreen({ navigation, route }: Props) {
  const { childId } = route.params;
  const { accessToken, activeChild, kidMode } = useAuthStore();
  const insets = useSafeAreaInsets();
  const [rows, setRows] = useState<RowData[]>([]);
  const [child, setChild] = useState<Child | null>(activeChild);
  const [focusedRow, setFocusedRow] = useState(0);
  const [focusedCol, setFocusedCol] = useState(0);
  // Real activity, not placeholders. These three were `useState(11)`,
  // `useState(23)` and `useState(60)` with no setter, so every child on every
  // day was told "11 stars today · 23 / 60 min" — while ParentHome showed the
  // same child's actual 0 stars and 0/60 min. Two screens, one child, two
  // different truths, and the one the kid sees was the invented one.
  const [progress, setProgress] = useState<Progress | null>(null);
  const stars = progress?.correctAnswers ?? 0;
  const usedMinutes = Math.round((progress?.todayWatchedSeconds ?? 0) / 60);
  const limitMinutes = child?.controls?.dailyMinutes ?? 60;
  // Set when at least one of the load calls rejected — lets the empty state
  // distinguish "no videos yet" from "couldn't reach the backend".
  const [loadFailed, setLoadFailed] = useState(false);
  // Suppresses the empty state on first mount until the load resolves, so the
  // "Nothing here yet" view doesn't flash for a few hundred ms before rows arrive.
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const flatListRefs = useRef<Map<number, FlatList>>(new Map());
  const scrollViewRef = useRef<ScrollView>(null);
  // Measured Y of each row section (via onLayout) so the focused row scrolls
  // into view exactly — a fixed per-row stride guesses wrong once real row
  // heights diverge, which left the focused row stuck below the fold on TV.
  const rowOffsets = useRef<Map<number, number>>(new Map());
  // Web/TV: refs to each row's underlying DOM node. RN-web's ScrollView.scrollTo
  // is a no-op here (the list overflows the page rather than an internally
  // scrolling box), so we scrollIntoView() the focused row's node instead.
  const rowViewRefs = useRef<Map<number, any>>(new Map());
  // TV horizontal nav: remember each row's focused column so moving up/down and
  // back restores where the row was, and so each row's translateX is driven
  // independently.
  const rowColRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;

    const toRowItem = (v: Video): VideoListItem => ({
      id: v.id,
      title: v.title,
      thumbUrl: v.thumbUrl,
      duration: v.duration,
      categories: v.categories,
      isPremium: v.isPremium,
      ageMin: 0,
      ageMax: 12,
    });

    const loadData = async () => {
      const allRows: RowData[] = [];

      // The category rail MUST come from this child's parental controls. It
      // used to be a hardcoded `categories: "numbers,letters,shapes"`, which
      // meant the "Allowed categories" control in Controls had no effect on
      // this screen at all — a child restricted to Bedtime was still served
      // (and could play) Numbers and Letters videos.
      //
      // `childrenApi.list` already returns `controls`, so no extra request:
      // only the rail waits on it, while recs/continue-watching stay parallel.
      const listP = childrenApi.list(accessToken);
      const allowedP = listP.then(
        (cs) => cs.find((c) => c.id === childId)?.controls?.categories ?? [],
      );
      const [list, recs, cont, mix, allowed, prog] = await Promise.allSettled([
        listP,
        childrenApi.getRecommendations(childId, accessToken),
        childrenApi.getContinueWatching(childId, accessToken),
        allowedP.then((cats) =>
          contentApi.list(
            cats.length ? { categories: cats.join(",") } : {},
            accessToken,
          ),
        ),
        allowedP,
        childrenApi.getProgress(childId, accessToken),
      ]);

      if (cancelled) return;

      // Stars + minutes-watched for the header pills. Left null on failure so
      // the pills read 0 rather than a stale or invented number.
      if (prog.status === "fulfilled") setProgress(prog.value);

      const allowedCats =
        allowed.status === "fulfilled" ? allowed.value : [];

      // If everything content-related rejected, surface that as a network
      // error rather than the generic "nothing here yet" empty state.
      const allRejected =
        recs.status === "rejected" &&
        cont.status === "rejected" &&
        mix.status === "rejected";
      setLoadFailed(allRejected);

      if (list.status === "fulfilled") {
        const found = list.value.find((c) => c.id === childId);
        if (found) setChild(found);
      }

      if (recs.status === "fulfilled" && recs.value.length > 0) {
        allRows.push({
          key: "forYou",
          title: "For you",
          items: recs.value.map(toRowItem),
        });
      }

      if (cont.status === "fulfilled" && cont.value.length > 0) {
        allRows.push({
          key: "continue",
          title: "Continue watching",
          items: cont.value.map(toRowItem),
        });
      }

      if (mix.status === "fulfilled" && mix.value.videos.length > 0) {
        allRows.push({
          key: "categoryMix",
          // Title follows the actual categories, so it can't claim to be
          // "Numbers, Letters & Shapes" while showing a Bedtime-only shelf.
          title: railTitle(allowedCats),
          items: mix.value.videos,
        });
      }

      // Empty state fallback: a brand-new account has no recommendations,
      // no continue-watching, and may not match the category mix. Show a
      // "Browse" row with every video so the screen isn't blank.
      if (allRows.length === 0) {
        try {
          // Still scoped to the allowed categories. An unscoped `list({})` here
          // was a second way around the parental control: any child whose
          // restriction produced no recs / no continue-watching / no rail got a
          // "Browse all" shelf containing the entire catalog.
          const all = await contentApi.list(
            allowedCats.length ? { categories: allowedCats.join(",") } : {},
            accessToken,
          );
          if (!cancelled && all.videos.length > 0) {
            allRows.push({
              key: "browse",
              title: "Browse all",
              items: all.videos,
            });
            // If the fallback succeeded, we DID reach the backend — clear
            // any "failed" flag set above.
            setLoadFailed(false);
          } else if (!cancelled && all.videos.length === 0) {
            // Backend responded with zero videos → genuinely empty catalog.
            setLoadFailed(false);
          }
        } catch {
          // Fallback also rejected — keep loadFailed true for the error UI.
          if (!cancelled) setLoadFailed(true);
        }
      }

      setRows(allRows);
      setLoading(false);
    };

    loadData().catch(() => {
      if (!cancelled) {
        setRows([]);
        setLoadFailed(true);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [childId, accessToken, reloadKey]);

  useEffect(() => {
    const currentRow = rows[focusedRow];
    if (currentRow) {
      const clampedCol = Math.min(focusedCol, currentRow.items.length - 1);
      if (clampedCol !== focusedCol) setFocusedCol(Math.max(0, clampedCol));
    }
  }, [focusedRow, focusedCol, rows]);

  useEffect(() => {
    // Remote-driven focus scrolling is a TV concern. On a phone there is no
    // D-pad, so running it just yanked each rail to `focusedCol` and left the
    // first card sliced off at the left edge — rails should start at 0 and be
    // swiped by hand.
    if (IS_PHONE) return;
    const ref = flatListRefs.current.get(focusedRow);
    if (ref) {
      ref.scrollToIndex({
        index: focusedCol,
        animated: true,
        viewPosition: 0.1,
      });
    }
  }, [focusedRow, focusedCol]);

  useEffect(() => {
    // Web/TV: scroll the focused row's real DOM node into view. This is the
    // reliable path — RN-web's ScrollView.scrollTo never moved the list because
    // the content overflows the page rather than an internally-scrolling box.
    // scrollIntoView targets whatever the real scroll container is.
    if (Platform.OS === "web") {
      const r = rowViewRefs.current.get(focusedRow);
      const node: any =
        r && typeof r.scrollIntoView === "function" ? r : findNodeHandle(r);
      if (node && typeof node.scrollIntoView === "function") {
        if (focusedRow === 0) {
          // The greeting header sits ABOVE the rows in the same scroll
          // container, so scrollIntoView on row 0 leaves it stranded off the
          // top. Walk up to the real scroll container and pin it to the very
          // top so the header comes back into view. NOTE: RN-web makes that
          // container `overflow: hidden` (it still scrolls programmatically),
          // so match on actual overflow — scrollHeight > clientHeight — NOT on
          // overflowY being auto/scroll, which is what missed it before.
          const d: any = document;
          let p: any = node.parentElement;
          let scroller: any = null;
          while (p) {
            if (p.scrollHeight > p.clientHeight + 40) {
              scroller = p;
              break;
            }
            p = p.parentElement;
          }
          scroller = scroller || d.scrollingElement || d.documentElement;
          if (scroller?.scrollTo)
            scroller.scrollTo({ top: 0, behavior: "smooth" });
          else if (scroller) scroller.scrollTop = 0;
        } else {
          node.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
        return;
      }
    }
    // Native: scroll the vertical ScrollView to the focused row's measured Y
    // (falling back to a per-row stride before onLayout has reported in).
    if (scrollViewRef.current) {
      const strideFallback = IS_PHONE
        ? ROW_TITLE_FS + 8 + THUMB_H + 24 + 10
        : ROW_TITLE_FS + 24 + THUMB_H + 24 + 64;
      const measured = rowOffsets.current.get(focusedRow);
      const y = measured != null ? measured : focusedRow * strideFallback;
      scrollViewRef.current.scrollTo({
        y: Math.max(0, y - (IS_PHONE ? 12 : 40)),
        animated: true,
      });
    }
  }, [focusedRow]);

  // Kid-first vs parent-browsing. With kid mode ON (a child is using the app)
  // a tap plays immediately — a 2–7-year-old can't read a synopsis and every
  // extra tap is a barrier. With kid mode OFF the parent is browsing, so the
  // same card opens the video's info page (poster, description, Play) first.
  //
  // TV is excluded on purpose: it also runs with kidMode false, but this screen
  // lives in ChildStack there, which has no ParentPreviewPlayer route — routing
  // to it would throw.
  const goPlay = useCallback(
    (videoId: string) => {
      if (!kidMode && !isFactorTv) {
        (navigation as any).navigate("ParentPreviewPlayer", { videoId });
        return;
      }
      navigation.navigate("Player", { childId, videoId });
    },
    [navigation, childId, kidMode],
  );

  // Disable the iOS swipe-back gesture in kid mode so a curious kid can't
  // edge-swipe back to the parent screens. The lock badge stays the only way out.
  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !kidMode });
  }, [navigation, kidMode]);

  useKeydown({
    isActive: true,
    up: () => {
      const nr = Math.max(0, focusedRow - 1);
      if (nr === focusedRow) return;
      setFocusedRow(nr);
      setFocusedCol(rowColRef.current.get(nr) ?? 0);
    },
    down: () => {
      const nr = Math.min(rows.length - 1, focusedRow + 1);
      if (nr === focusedRow) return;
      setFocusedRow(nr);
      setFocusedCol(rowColRef.current.get(nr) ?? 0);
    },
    left: () => {
      const nc = Math.max(0, focusedCol - 1);
      setFocusedCol(nc);
      rowColRef.current.set(focusedRow, nc);
    },
    right: () => {
      const cur = rows[focusedRow];
      if (!cur) return;
      const nc = Math.min(cur.items.length - 1, focusedCol + 1);
      setFocusedCol(nc);
      rowColRef.current.set(focusedRow, nc);
    },
    ok: () => {
      const cur = rows[focusedRow];
      const item = cur?.items[focusedCol];
      if (item) goPlay(item.id);
    },
    // Back returns to the "Who's watching?" profile picker.
    // `replace` rather than `goBack` because Child Home is typically the
    // initial route after pairing/profile-pick; we want a clean stack.
    // In *mobile kid mode* the lock badge is the only sanctioned way out —
    // swallow hardware back so a kid can't accidentally bail to the parent
    // app. On TV there is no parent app to bail to, so Back always returns
    // to the profile picker.
    back: () => {
      if (kidMode && !isFactorTv) return;
      navigation.replace("ProfileSelect");
    },
  });

  const renderCard = useCallback(
    (item: VideoListItem, index: number, rowIndex: number) => {
      const isFocused = focusedRow === rowIndex && focusedCol === index;
      // The focus highlight (border + scale) is a TV/D-pad affordance only.
      // On a touch phone there's no "focused" card, and focusedRow/Col default
      // to (0,0) — which would otherwise pin the highlight on the first card.
      const showFocus = isFocused && isFactorTv;
      const category = item.categories[0] ?? "";
      const { palette, icon } = paletteFor(category);

      return (
        <TouchableOpacity
          key={item.id}
          onPress={() => goPlay(item.id)}
          activeOpacity={0.85}
          style={[
            styles.thumbWrap,
            { transform: [{ scale: showFocus ? 1.06 : 1 }] },
          ]}
        >
          <View style={[styles.thumb, showFocus && styles.thumbFocused]}>
            <VideoArt
              icon={icon}
              palette={palette}
              width={THUMB_W}
              height={THUMB_H}
            />
            {item.isPremium && (
              <View style={styles.lockBadge}>
                <Lock size={20} color="#fff" strokeWidth={2} style={styles.lockIcon} />
              </View>
            )}
            <View style={styles.cardOverlay}>
              <CardScrim
                opacityMid={IS_PHONE ? 0.22 : 0.34}
                opacityBottom={IS_PHONE ? 0.62 : 0.8}
              />
              <Text style={styles.cardCategory}>
                {(category || "Video").toUpperCase()}
              </Text>
              <Text style={styles.cardTitle} numberOfLines={2}>
                {item.title}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    },
    [focusedRow, focusedCol, goPlay],
  );

  const childName = child?.name ?? activeChild?.name ?? "Friend";
  const childColor = child?.color ?? PROFILE_THEMES[0].color;
  const theme = {
    color: childColor,
    bg: hexToSoftBg(childColor),
  };

  return (
    <View style={styles.container}>
      <View style={[styles.topBar, { paddingTop: TOP_PAD + insets.top }]}>
        {/* Tapping the avatar/name returns to "Who's watching?" so a kid can
            switch profiles. The chevron hints it's interactive. The parent
            lock (KidModeLock, top-right) stays PIN-gated and separate. */}
        <TouchableOpacity
          style={styles.topLeft}
          activeOpacity={0.7}
          onPress={() => navigation.replace("ProfileSelect")}
          accessibilityLabel="Switch profile"
          accessibilityRole="button"
        >
          <View style={[styles.avatarRing, { backgroundColor: theme.bg }]}>
            <View style={styles.avatarInner}>
              <Mascot mood="happy" color={theme.color} size={MASCOT_SIZE} />
            </View>
          </View>
          <View>
            <Text style={styles.welcome}>WELCOME BACK</Text>
            <View style={styles.greetingRow}>
              <Text style={styles.greeting}>Hi, {childName}!</Text>
              <ChevronDown
                size={IS_PHONE ? 20 : 30}
                color={colors.ink2}
                strokeWidth={2.6}
                style={styles.greetingChevron}
              />
            </View>
          </View>
        </TouchableOpacity>
        <View style={styles.topRight}>
          <View style={styles.starPill}>
            <Star size={STAR_BOX} color="#B45A24" fill="#FFD86B" strokeWidth={1.4} style={styles.starIcon} />
            <Text style={styles.starText}>{stars} stars today</Text>
          </View>
          <View style={styles.clockRow}>
            <Clock size={CLOCK_BOX} color="#6B5A48" strokeWidth={2} style={styles.clockIcon} />
            <Text style={styles.clockText}>
              {usedMinutes} / {limitMinutes} min
            </Text>
          </View>
        </View>
      </View>

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollArea}
        showsVerticalScrollIndicator={false}
      >
        {/* Empty state — splits into two messages depending on whether the
            problem is "backend unreachable" or "backend says zero videos".
            Gated on !loading so it doesn't flash before the first fetch resolves. */}
        {!loading && rows.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>
              {loadFailed ? "Can't reach Kidly" : "Nothing here yet"}
            </Text>
            <Text style={styles.emptyBody}>
              {loadFailed
                ? "Check your connection and try again."
                : "Ask a grown-up to add some videos."}
            </Text>
            <TouchableOpacity
              style={styles.retryBtn}
              activeOpacity={0.85}
              onPress={() => {
                setLoading(true);
                setReloadKey((k) => k + 1);
              }}
            >
              <Text style={styles.retryBtnText}>Try again</Text>
            </TouchableOpacity>
          </View>
        )}
        {rows.map((row, rowIndex) => {
          const col =
            rowIndex === focusedRow
              ? focusedCol
              : rowColRef.current.get(rowIndex) ?? 0;
          const translateX = -Math.max(0, col) * CARD_STRIDE;
          return (
            <View
              key={row.key}
              style={styles.rowSection}
              ref={(r) => {
                if (r) rowViewRefs.current.set(rowIndex, r);
              }}
              onLayout={(e) => {
                rowOffsets.current.set(rowIndex, e.nativeEvent.layout.y);
              }}
            >
              <Text style={styles.rowTitle}>{row.title}</Text>
              {isFactorTv ? (
                // TV: move the row with a GPU translateX + CSS transition.
                // RN-web's FlatList.scrollToIndex never actually scrolled on
                // Tizen (same no-op as the vertical ScrollView.scrollTo), so
                // pressing right moved the focus highlight but not the row, and
                // what little moved was janky. A transformed track is smooth on
                // Chromium 76 and always responds to the d-pad.
                <View style={styles.rowViewport}>
                  <View
                    style={[
                      styles.rowTrack,
                      {
                        transform: [{ translateX }],
                        transitionProperty: "transform",
                        transitionDuration: "300ms",
                        transitionTimingFunction: "ease-out",
                      } as any,
                    ]}
                  >
                    {row.items.map((item, index) =>
                      renderCard(item, index, rowIndex),
                    )}
                  </View>
                </View>
              ) : (
                <FlatList
                  ref={(ref) => {
                    if (ref) flatListRefs.current.set(rowIndex, ref);
                  }}
                  horizontal
                  data={row.items}
                  keyExtractor={(v) => `${row.key}-${v.id}`}
                  showsHorizontalScrollIndicator={false}
                  renderItem={({ item, index }) =>
                    renderCard(item, index, rowIndex)
                  }
                  contentContainerStyle={styles.rowContent}
                  style={{ overflow: "visible" as const }}
                  onScrollToIndexFailed={() => {}}
                />
              )}
            </View>
          );
        })}
        <View style={{ height: 40 }} />
      </ScrollView>
      {/* Mounts only when kidMode is on — no-op on the TV/standalone child flow. */}
      <KidModeLock />
    </View>
  );
}

function hexToSoftBg(hex: string): string {
  const m = hex
    .replace("#", "")
    .match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return "#FFE2C2";
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},0.22)`;
}

const SCREEN_W = Dimensions.get("window").width;
const IS_PHONE = Platform.OS !== "web" && SCREEN_W < 768;

// TV-scale on big screens; tighter dimensions on a phone so cards & header fit.
const PADDING_X = IS_PHONE ? 20 : 80;
// Phone: ~2 cards visible per row (was ~1.5 before — too big).
// TV: stays generously sized for couch distance.
const THUMB_W = IS_PHONE ? 200 : 420;
const THUMB_H = IS_PHONE ? 120 : 260;
// Horizontal stride per card (card width + its right margin) — the TV row
// translateX shifts by this for each focused column.
const CARD_STRIDE = THUMB_W + (IS_PHONE ? 14 : 28);
const GREETING_FS = IS_PHONE ? 26 : 48;
const GREETING_LH = IS_PHONE ? 30 : 52;
const WELCOME_FS = IS_PHONE ? 11 : 20;
const ROW_TITLE_FS = IS_PHONE ? 18 : 32;
const AVATAR_SIZE = IS_PHONE ? 56 : 96;
const MASCOT_SIZE = IS_PHONE ? 50 : 88;
const TOP_PAD = IS_PHONE ? 16 : 44;
const PILL_FS = IS_PHONE ? 13 : 30;
const STAR_BOX = IS_PHONE ? 18 : 42;
const CLOCK_BOX = IS_PHONE ? 16 : 36;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  topBar: {
    flexDirection: IS_PHONE ? "column" : "row",
    alignItems: IS_PHONE ? "stretch" : "center",
    justifyContent: "space-between",
    paddingHorizontal: PADDING_X,
    paddingTop: TOP_PAD,
    paddingBottom: IS_PHONE ? 16 : 28,
  },
  topLeft: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: IS_PHONE ? 16 : 0,
  },
  avatarRing: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginRight: IS_PHONE ? 12 : 24,
  },
  avatarInner: {
    marginTop: IS_PHONE ? 8 : 14,
  },
  welcome: {
    fontFamily,
    fontSize: WELCOME_FS,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
  },
  greeting: {
    fontFamily,
    fontSize: GREETING_FS,
    fontWeight: "900",
    color: colors.ink,
    lineHeight: GREETING_LH,
    letterSpacing: -0.5,
  },
  greetingRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  greetingChevron: {
    marginLeft: 4,
    marginTop: 2,
  },
  topRight: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: IS_PHONE ? "space-between" : "flex-start",
  },
  starPill: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: IS_PHONE ? 12 : 22,
    paddingVertical: IS_PHONE ? 8 : 14,
    borderRadius: 999,
    marginRight: IS_PHONE ? 12 : 32,
  },
  starIcon: {
    width: STAR_BOX,
    height: STAR_BOX,
    marginRight: IS_PHONE ? 6 : 10,
  },
  starText: {
    fontFamily,
    fontSize: PILL_FS,
    fontWeight: "800",
    color: colors.primaryInk,
  },
  clockRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  clockIcon: {
    width: CLOCK_BOX,
    height: CLOCK_BOX,
    marginRight: IS_PHONE ? 6 : 10,
  },
  clockText: {
    fontFamily,
    fontSize: IS_PHONE ? 13 : 28,
    fontWeight: "700",
    color: colors.ink2,
  },
  scrollArea: { flex: 1 },
  emptyState: {
    paddingHorizontal: PADDING_X,
    paddingVertical: IS_PHONE ? 40 : 64,
    alignItems: "center",
  },
  emptyTitle: {
    fontFamily,
    fontSize: IS_PHONE ? 18 : 28,
    fontWeight: "900",
    color: colors.ink,
    marginBottom: 6,
  },
  emptyBody: {
    fontFamily,
    fontSize: IS_PHONE ? 14 : 20,
    fontWeight: "600",
    color: colors.ink2,
    textAlign: "center",
  },
  retryBtn: {
    marginTop: 16,
    paddingHorizontal: 22,
    paddingVertical: 12,
    backgroundColor: colors.ink,
    borderRadius: 999,
  },
  retryBtnText: {
    fontFamily,
    fontSize: IS_PHONE ? 14 : 18,
    fontWeight: "800",
    color: "#fff",
  },
  rowSection: {
    marginBottom: IS_PHONE ? 10 : 64,
    overflow: "visible" as const,
  },
  rowTitle: {
    fontFamily,
    fontSize: ROW_TITLE_FS,
    fontWeight: "900",
    color: colors.ink,
    paddingHorizontal: PADDING_X,
    marginBottom: IS_PHONE ? 8 : 18,
  },
  rowContent: {
    paddingHorizontal: PADDING_X,
    paddingVertical: 12,
    overflow: "visible" as const,
  },
  rowViewport: {
    // The transformed track is clipped to this box so off-screen cards don't
    // bleed out or create page-level horizontal scroll. Vertical padding keeps
    // the focused card's scale + border from being clipped.
    overflow: "hidden",
    paddingVertical: IS_PHONE ? 8 : 30,
  },
  rowTrack: {
    flexDirection: "row",
    paddingHorizontal: PADDING_X,
  },
  thumbWrap: {
    marginRight: IS_PHONE ? 14 : 28,
    // Shadow lives on the wrapper because `thumb` uses overflow:hidden to clip
    // the art to the rounded corners — and iOS won't render a shadow on a view
    // that clips its children.
    shadowColor: "#000",
    shadowOpacity: IS_PHONE ? 0.18 : 0.1,
    shadowOffset: { width: 0, height: IS_PHONE ? 8 : 6 },
    shadowRadius: IS_PHONE ? 14 : 16,
    elevation: IS_PHONE ? 6 : 4,
  },
  thumb: {
    width: THUMB_W,
    height: THUMB_H,
    borderRadius: 18,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  thumbFocused: {
    borderWidth: 5,
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOpacity: 0.32,
    shadowRadius: 44,
    shadowOffset: { width: 0, height: 22 },
    elevation: 12,
  },
  lockBadge: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(0,0,0,0.4)",
    alignItems: "center",
    justifyContent: "center",
  },
  lockIcon: { width: 20, height: 20 },
  cardOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: IS_PHONE ? Math.round(THUMB_H * 0.65) : Math.round(THUMB_H * 0.55),
    paddingHorizontal: IS_PHONE ? 12 : 22,
    paddingBottom: IS_PHONE ? 10 : 20,
    justifyContent: "flex-end",
    overflow: "hidden",
  },
  cardCategory: {
    fontFamily,
    fontSize: IS_PHONE ? 10 : 20,
    fontWeight: "800",
    color: "rgba(255,255,255,0.95)",
    letterSpacing: 1.2,
    // Belt-and-suspenders with the scrim — drop shadow on the glyphs themselves
    // gives definition on lighter pastel cards (green, light blue).
    textShadowColor: "rgba(0,0,0,0.55)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: IS_PHONE ? 3 : 4,
  },
  cardTitle: {
    fontFamily,
    fontSize: IS_PHONE ? 14 : 28,
    fontWeight: "900",
    color: "#fff",
    lineHeight: IS_PHONE ? 18 : 32,
    marginTop: 4,
    textShadowColor: "rgba(0,0,0,0.7)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: IS_PHONE ? 4 : 6,
  },
});

function buildScrimSvg(opacityMid: number, opacityBottom: number): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1" preserveAspectRatio="none">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="#000" stop-opacity="0"/>` +
    `<stop offset="0.5" stop-color="#000" stop-opacity="${opacityMid}"/>` +
    `<stop offset="1" stop-color="#000" stop-opacity="${opacityBottom}"/>` +
    `</linearGradient></defs>` +
    `<rect width="1" height="1" fill="url(#g)"/>` +
    `</svg>`
  );
}

type CardScrimProps = { opacityMid: number; opacityBottom: number };

function CardScrim({ opacityMid, opacityBottom }: CardScrimProps) {
  const svg = useMemo(
    () => buildScrimSvg(opacityMid, opacityBottom),
    [opacityMid, opacityBottom],
  );
  if (Platform.OS === "web") {
    const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return (
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Image
          source={{ uri }}
          style={StyleSheet.absoluteFill}
          resizeMode="stretch"
        />
      </View>
    );
  }
  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      <SvgXml xml={svg} width="100%" height="100%" preserveAspectRatio="none" />
    </View>
  );
}
